import { isAxiosError } from 'axios';

import { VOTING_PERIOD, VOTING_SLOTS_LIMIT } from 'constants/dao';
import { DAY } from 'constants/intervals';

import {
    ProposalQueueResponse,
    ProposalQueueSlot,
    ProposalSimple,
    UpcomingVoteData,
    VotingSlot,
} from 'types/governance';

const isAssetProposalType = (
    proposalType?: string | null,
): proposalType is 'ADD_ASSET' | 'REMOVE_ASSET' =>
    proposalType === 'ADD_ASSET' || proposalType === 'REMOVE_ASSET';

const PROPOSAL_QUEUE_SLOT_CONFLICT_CODE = 'proposal_queue_slot_conflict';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const getCurrentMondayUtc = () => {
    const now = new Date();
    const todayUtc = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
    const dayOfWeek = new Date(todayUtc).getUTCDay();
    const daysSinceMonday = (dayOfWeek + 6) % 7;

    return todayUtc - daysSinceMonday * DAY;
};

const getSlotId = (timestamp: number) => new Date(timestamp).toISOString().slice(0, 10);

export const toUtcSecondIso = (timestamp: number) =>
    new Date(timestamp).toISOString().replace('.000Z', 'Z');

const getTimestamp = (value: string) => new Date(value).getTime();

const getProposalsMap = (proposals: ProposalSimple[]) =>
    proposals.reduce<Record<number, ProposalSimple>>((acc, proposal) => {
        acc[proposal.id] = proposal;
        return acc;
    }, {});

const getQueueSlotsMap = (slots: ProposalQueueSlot[]) =>
    slots.reduce<Record<number, ProposalQueueSlot>>((acc, slot) => {
        acc[getTimestamp(slot.start_at)] = slot;
        return acc;
    }, {});

export const getVotingSlotsFromQueue = (
    queue: ProposalQueueResponse,
    proposals: ProposalSimple[] = [],
): VotingSlot[] => {
    // Booking starts from the week after the current one; the current week is not shown.
    const firstMonday = getCurrentMondayUtc() + VOTING_PERIOD;
    const slotsCount = VOTING_SLOTS_LIMIT;
    const queueSlotsMap = getQueueSlotsMap(queue.results);
    const proposalsMap = getProposalsMap(proposals);

    return Array.from({ length: slotsCount }, (_, index) => {
        const startTimestamp = firstMonday + index * VOTING_PERIOD;
        const endTimestamp = startTimestamp + VOTING_PERIOD - 1000;
        const queueSlot = queueSlotsMap[startTimestamp] ?? null;
        const proposal = queueSlot ? proposalsMap[queueSlot.proposal] : null;
        const isAsset = isAssetProposalType(proposal?.proposal_type);

        return {
            id: queueSlot ? String(queueSlot.proposal) : getSlotId(startTimestamp),
            start_at: toUtcSecondIso(startTimestamp),
            end_at: toUtcSecondIso(endTimestamp),
            status: queueSlot ? 'TAKEN' : 'AVAILABLE',
            proposal_id: queueSlot?.proposal ?? null,
            proposal_title: queueSlot?.proposal_title ?? proposal?.title ?? null,
            proposal_status: queueSlot?.proposal_status ?? null,
            proposal_type: proposal?.proposal_type ?? null,
            asset_code: isAsset ? (proposal?.asset_code ?? null) : null,
            asset_issuer: isAsset ? (proposal?.asset_issuer ?? null) : null,
            asset_contract_address: isAsset ? (proposal?.asset_contract_address ?? null) : null,
            occupied_at: queueSlot?.occupied_at ?? null,
        };
    });
};

export const isVotingSlotAvailable = (slot: VotingSlot) => slot.status === 'AVAILABLE';

export const getAvailableVotingSlots = (slots: VotingSlot[]) => slots.filter(isVotingSlotAvailable);

const formatUtcDate = (value: string, withoutYear = false) => {
    const date = new Date(value);
    const month = MONTHS[date.getUTCMonth()];
    const day = date.getUTCDate();
    const year = date.getUTCFullYear();

    return `${month} ${day}${withoutYear ? '' : `, ${year}`}`;
};

export const getVotingSlotStartLabel = (slot: VotingSlot) => formatUtcDate(slot.start_at, true);

export const getUpcomingVoteFromSlot = (slot: VotingSlot): UpcomingVoteData | null => {
    if (slot.status !== 'TAKEN' || slot.proposal_status !== 'QUEUED') {
        return null;
    }

    const base = {
        id: slot.proposal_id ? String(slot.proposal_id) : undefined,
        startsAt: `Starts ${getVotingSlotStartLabel(slot)}`,
    };

    if (
        isAssetProposalType(slot.proposal_type) &&
        (slot.asset_code || slot.asset_contract_address)
    ) {
        return {
            ...base,
            proposal_type: slot.proposal_type,
            title: slot.proposal_title ?? `${slot.asset_code ?? ''} asset registry vote`,
            assetCode: slot.asset_code ?? '',
            assetIssuer: slot.asset_issuer ?? '',
            assetContract: slot.asset_contract_address ?? '',
        };
    }

    if (slot.proposal_id) {
        return {
            ...base,
            proposal_type: slot.proposal_type ?? 'GENERAL',
            title: slot.proposal_title ?? `Proposal #${slot.proposal_id}`,
        };
    }

    return null;
};

export const getUpcomingVotesFromSlots = (slots: VotingSlot[]): UpcomingVoteData[] =>
    slots.map(getUpcomingVoteFromSlot).filter((item): item is UpcomingVoteData => Boolean(item));

export const getVotingSlotRangeLabel = (slot: VotingSlot) =>
    `${formatUtcDate(slot.start_at, true)} – ${formatUtcDate(slot.end_at)}`;

export const isProposalQueueSlotConflict = (error: unknown) =>
    isAxiosError(error) &&
    error.response?.status === 409 &&
    (error.response.data as { code?: string } | undefined)?.code ===
        PROPOSAL_QUEUE_SLOT_CONFLICT_CODE;
